import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import LimitsForm from "../components/LimitsForm";
import AdvancedForm from "../components/AdvancedMenu";
import EventAvailabilityForm from "../components/EventAvailability";
import PaymentSettings from "../components/PaymentSettings";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

axios.defaults.withCredentials = true;
axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
  "token",
)}`;
export const showToast = (title, icon = "success") => {
  Swal.fire({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title,
  });
};

function EventForm({ isEdit = false, userId }) {
  const [formData, setFormData] = useState({
    title: "",
    duration: "",
    slug: "",
    description: "",
    visibility: "public",
    is_active: true,
    is_hidden: false,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    min_notice_minutes: 0,
    max_future_days: 30,
    daily_booking_limit: "",
    redirect_url: "",
  });
  const [activeSection, setActiveSection] = useState("basic");
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionRefs = useRef({});

  useEffect(() => {
    if (isEdit && id) {
      axios
        .get(`${API_BASE_URL}/events/${id}`)
        .then((res) => {
          const data = res.data;

          // --- FIX 1: Ensure locationDetail is always set ---
          setFormData({
            ...data,
            daily_booking_limit: data.daily_booking_limit ?? "",
            locationDetail: data.location_detail || "", // ← Critical!
          });
        })
        .catch(() => showToast("Failed to load event data.", "error"));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: [
        "duration",
        "buffer_before_minutes",
        "buffer_after_minutes",
        "min_notice_minutes",
        "max_future_days",
      ].includes(name)
        ? parseInt(value)
        : name === "daily_booking_limit"
          ? value === ""
            ? ""
            : parseInt(value)
          : value,
    }));
  };

  const handleCheckboxChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.checked });

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = {
      ...formData,
      daily_booking_limit:
        formData.daily_booking_limit === ""
          ? null
          : parseInt(formData.daily_booking_limit),
    };

    if (isEdit) {
      // Make the API call for updating the event
      axios
        .put(`${API_BASE_URL}/events/${id}`, payload)
        .then((response) => {
          // Handle successful update
          showToast("Event updated successfully");
        })
        .catch((error) => {
          console.error("Error updating event:", error);
        });
    } else {
      // Make the API call for creating the event
      axios
        .post(`${API_BASE_URL}/events`, payload)
        .then((response) => {
          // Handle successful creation
          console.log("Event created successfully");
        })
        .catch((error) => {
          console.error("Error creating event:", error);
        });
    }
  };
  const limitsRef = useRef(null);
  const basicRef = useRef(null);
  const paymentsRef = useRef(null);
  const visibilityRef = useRef(null);
  const availabilityRef = useRef(null);
  const advancedRef = useRef(null);

  const sections = [
    { key: "basic", label: "Basic Info", icon: "bi-pencil" },
    { key: "availability", label: "Availability", icon: "bi-calendar-check" },
    { key: "limits", label: "Limits", icon: "bi-sliders" },
    { key: "advanced", label: "Advanced", icon: "bi-gear" },
    { key: "payments", label: "Payments", icon: "bi-credit-card" },
  ];

  useEffect(() => {
    if (activeSection === "limits" && limitsRef.current) {
      limitsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeSection]);
  return (
    <div className="container-fluid">
      <div className="row">
        {/* Sticky Sidebar */}
        <nav className="col-md-3 col-lg-2 d-md-block bg-light sidebar collapse show position-sticky top-0 vh-100">
          <div className="pt-3">
            <ul className="nav flex-column dep-nav">
              {sections.map((sec) => (
                <li className="nav-item" key={sec.key}>
                  <button
                    className={`nav-link btn btn-link text-start w-100 ${
                      activeSection === sec.key ? "fw-bold text-primary" : ""
                    }`}
                    onClick={() => setActiveSection(sec.key)}
                  >
                    <i className={`bi ${sec.icon} me-2`}></i>
                    {sec.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main Form */}
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 mt-3">
          {activeSection === "basic" && (
            <section ref={basicRef} id="basicSection" className="col-md-9">
              <div className="title-line2">
                <h5 className="mb-0 fw700">
                  {isEdit ? "Edit Event" : "Create Event"}
                </h5>
              </div>
              <form onSubmit={handleSubmit}>
                {/* BASIC INFO COLLAPSE */}
                <div className="accordion mb-4" id="eventAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="basicHeading">
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#basicCollapse"
                        aria-expanded="true"
                        aria-controls="basicCollapse"
                      >
                        Basic Info
                      </button>
                    </h2>
                    <div
                      id="basicCollapse"
                      className="accordion-collapse collapse show"
                      aria-labelledby="basicHeading"
                      data-bs-parent="#eventAccordion"
                    >
                      <div className="accordion-body">
                        <div ref={(el) => (sectionRefs.current.basic = el)}>
                          <div className="row">
                            <div className="col-md-6 col-12">
                              <div className="form-group mb-3">
                                <label>Title</label>
                                <input
                                  className="form-control"
                                  name="title"
                                  value={formData.title}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-md-6 col-12">
                              <div className="form-group mb-3">
                                <label>Slug</label>
                                <input
                                  className="form-control"
                                  name="slug"
                                  value={formData.slug}
                                  onChange={handleChange}
                                  required
                                  disabled={isEdit}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="form-group mb-3">
                            <label>Description</label>
                            <textarea
                              className="form-control"
                              name="description"
                              rows="3"
                              value={formData.description}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* TIMING COLLAPSE */}
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="timingHeading">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#timingCollapse"
                        aria-expanded="false"
                        aria-controls="timingCollapse"
                      >
                        Timing
                      </button>
                    </h2>
                    <div
                      id="timingCollapse"
                      className="accordion-collapse collapse"
                      aria-labelledby="timingHeading"
                      data-bs-parent="#eventAccordion"
                    >
                      <div className="accordion-body">
                        <div ref={(el) => (sectionRefs.current.timing = el)}>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label>Duration (minutes)</label>
                              <input
                                className="form-control"
                                type="number"
                                name="duration"
                                value={formData.duration}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LOCATION COLLAPSE */}
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="locationHeading">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#locationCollapse"
                        aria-expanded="false"
                        aria-controls="locationCollapse"
                      >
                        Location
                      </button>
                    </h2>
                    <div
                      id="locationCollapse"
                      className="accordion-collapse collapse"
                      aria-labelledby="locationHeading"
                      data-bs-parent="#eventAccordion"
                    >
                      <div className="accordion-body">
                        <div className="row">
                          <div className="col-md-6 col-12">
                            <div className="form-group mb-3">
                              <label>Location</label>
                              <select
                                className="form-select"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                              >
                                <option value="">Select a location</option>
                                <option value="TickTickGo Meet">
                                  TickTickGo Meet
                                </option>
                                <option value="zoom">TickTickGo Zoom</option>
                                <option value="google">Google Meet</option>
                                {/* <option value="teams">Microsoft Teams</option>
                                <option value="webex">Webex</option>
                                <option value="gotomeeting">GoToMeeting</option> */}
                                <option value="Meeting Link">Custom Meeting Link</option>
                                <option value="In-Person">In-Person</option>
                                <option value="Phone">Phone</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Conditional input for location details */}
                        {formData.location && !["TickTickGo Meet", "google", "zoom", "teams", "webex", "gotomeeting"].includes(formData.location) && (
                          <div className="form-group mb-3">
                            <label>
                              Provide the {formData.location} details
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              name="locationDetail"
                              value={formData.locationDetail || ""}
                              onChange={handleChange}
                              placeholder={`Enter ${formData.location} details`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <button className="btn btn-success mb-4" type="submit">
                  {isEdit ? "Update Event" : "Create Event"}
                </button>
              </form>
            </section>
          )}

          {activeSection === "availability" && (
            <section
              ref={availabilityRef}
              id="availabilitySection"
              className="col-md-9"
            >
              <EventAvailabilityForm userId={userId} />
            </section>
          )}
          {activeSection === "limits" && (
            <section ref={limitsRef} id="limitsSection" className="col-md-9">
              <LimitsForm eventId={id} />
            </section>
          )}
          {activeSection === "advanced" && (
            <section
              ref={advancedRef}
              id="advancedSection"
              className="col-md-9"
            >
              <AdvancedForm eventId={id} eventTitle={formData.title} />
            </section>
          )}
          {activeSection === "payments" && (
            <section
              ref={paymentsRef}
              id="paymentsSection"
              className="col-md-9"
            >
              <PaymentSettings eventId={id} />
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default EventForm;
